import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatusBadge, KIND_LABEL } from "./status";

const EvidenceView = ({ c }) => (
  <div className="text-[11px] text-stone-600 mt-1 space-y-0.5">
    {c.ref?.title && <div>Listing: <b>{c.ref.title}</b> ({c.ref.id})</div>}
    {c.ref?.type === "project" && <div>Project: <b>{c.ref.title}</b></div>}
    {Object.entries(c.evidence || {}).filter(([, v]) => v).map(([k, v]) => (
      <div key={k}>{k.replace(/_/g, " ")}: {k === "photo_url" ? <a className="underline" href={v} target="_blank" rel="noreferrer">open</a> : String(v)}</div>
    ))}
    {c.confirmer_mobile_masked && <div>Confirmer: {c.confirmer_mobile_masked}</div>}
    {(c.flags || []).length > 0 && <div className="text-orange-700">Flags: {c.flags.join(", ")}</div>}
  </div>
);

const Item = ({ c, onDone }) => {
  const [reason, setReason] = useState("");
  const [fake, setFake] = useState(false);
  const act = async (action) => {
    try {
      await api.post(`/civic/review/${c.contribution_id}`, { action, reason, confirm_fake: fake || undefined });
      toast.success(`Done: ${action.replace("_", " ")}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const btn = "text-[11px] font-black px-3 py-1.5 rounded-full";
  return (
    <li className="rounded-xl border border-stone-200 p-3" data-testid={`review-item-${c.contribution_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-bold text-sm">{KIND_LABEL[c.kind] || c.kind}</span>
        <StatusBadge status={c.status} />
        <span className="text-[11px] text-stone-500">by {c.claimant_name || "volunteer"} · as {c.review_as}</span>
        <span className="ml-auto text-xs font-black tabular-nums">{c.points} pts</span>
      </div>
      <EvidenceView c={c} />
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required except approve)"
        className="mt-2 w-full px-3 py-1.5 rounded-full border border-stone-200 text-xs" data-testid={`review-reason-${c.contribution_id}`} />
      {c.kind === "report_fake" && (
        <label className="flex items-center gap-2 text-[11px] mt-1">
          <input type="checkbox" checked={fake} onChange={(e) => setFake(e.target.checked)} data-testid={`review-confirm-fake-${c.contribution_id}`} />
          I confirmed this listing is fake (it will be hidden)
        </label>
      )}
      <div className="flex flex-wrap gap-1.5 mt-2">
        <button className={`${btn} bg-emerald-600 text-white`} onClick={() => act("approve")} data-testid={`review-approve-${c.contribution_id}`}>Approve</button>
        <button className={`${btn} bg-red-600 text-white`} onClick={() => act("reject")} data-testid={`review-reject-${c.contribution_id}`}>Reject</button>
        <button className={`${btn} bg-orange-100 text-orange-800`} onClick={() => act("request_evidence")} data-testid={`review-evidence-${c.contribution_id}`}>Request evidence</button>
        {c.status !== "disputed" && <button className={`${btn} bg-sky-100 text-sky-800`} onClick={() => act("dispute")} data-testid={`review-dispute-${c.contribution_id}`}>Dispute</button>}
      </div>
    </li>
  );
};

export const ReviewQueue = ({ isAdmin }) => {
  const [legacy, setLegacy] = useState(false);
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    api.get("/civic/review/queue", { params: { legacy } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [legacy]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="rounded-3xl bg-white border border-stone-200 p-5 mb-6" data-testid="vol-review-queue">
      <div className="flex items-center justify-between mb-3">
        <h2 className="heading text-lg font-black text-stone-900">Review queue</h2>
        {isAdmin && (
          <button className="text-[11px] font-black px-3 py-1 rounded-full bg-violet-100 text-violet-800" onClick={() => setLegacy(!legacy)} data-testid="review-legacy-toggle">
            {legacy ? "Show new claims" : "Show legacy claims"}
          </button>
        )}
      </div>
      {items.length === 0
        ? <div className="text-sm text-stone-400 py-4 text-center" data-testid="review-empty">No contributions waiting for you.</div>
        : <ul className="space-y-2">{items.map((c) => <Item key={c.contribution_id} c={c} onDone={load} />)}</ul>}
    </div>
  );
};
